/**
 * MediaImportPage — Import multiple photos+videos + affectation groupee.
 * Route : /dashboard/media-import
 *
 * Etape 1 : selection fichiers (multiple), upload batch via POST /api/media/upload-batch
 * Etape 2 : affectation groupee (album / article / trip+day / destination / partner / deal)
 *           via POST /api/media/batch-attach
 */
import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";
import {
  uploadMediaBatch,
  batchAttachMedia,
  getTripsAdmin,
  getTripBySlug,
} from "../lib/api";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { UploadCloud, X, Check, Image as ImageIcon, Video as VideoIcon, Loader2, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MediaImportPage() {
  const [step, setStep] = useState(1); // 1 = select+upload | 2 = affect
  const [files, setFiles] = useState([]);   // File[]
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState([]); // MediaResponse[]
  const [progressPct, setProgressPct] = useState(0);
  const inputRef = useRef(null);

  // Entity refs for step 2
  const [albums, setAlbums] = useState([]);
  const [articles, setArticles] = useState([]);
  const [trips, setTrips] = useState([]);
  const [partners, setPartners] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [deals, setDeals] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState({ type: "none", entity_id: null, day_id: null });
  const [tripDays, setTripDays] = useState([]);

  useEffect(() => {
    if (step === 2 && uploaded.length > 0) {
      // Load entity choices
      Promise.all([
        axios.get(`${API}/albums`).then((r) => setAlbums(r.data)).catch(() => setAlbums([])),
        axios.get(`${API}/articles?limit=200`).then((r) => setArticles(r.data)).catch(() => setArticles([])),
        getTripsAdmin().then((r) => setTrips(r.data)).catch(() => setTrips([])),
        axios.get(`${API}/partners`).then((r) => setPartners(r.data)).catch(() => setPartners([])),
        axios.get(`${API}/destinations`).then((r) => setDestinations(r.data)).catch(() => setDestinations([])),
        axios.get(`${API}/deals`).then((r) => setDeals(r.data)).catch(() => setDeals([])),
      ]);
    }
  }, [step, uploaded.length]);

  useEffect(() => {
    if (selectedEntity.type === "trip_day" && selectedEntity.entity_id) {
      const trip = trips.find((t) => t.id === selectedEntity.entity_id);
      if (trip) {
        getTripBySlug(trip.slug).then((r) => setTripDays(r.data.days || [])).catch(() => setTripDays([]));
      }
    }
  }, [selectedEntity.type, selectedEntity.entity_id, trips]);

  const onFileChange = (e) => {
    const list = Array.from(e.target.files || []);
    const acc = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif", "video/mp4", "video/quicktime", "video/webm"];
    const valid = list.filter((f) => acc.includes(f.type) || /\.(jpg|jpeg|png|webp|heic|heif|mp4|mov|webm)$/i.test(f.name));
    setFiles(valid);
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const doUpload = async () => {
    if (files.length === 0) { toast.error("Aucun fichier"); return; }
    setUploading(true);
    setProgressPct(0);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    try {
      const res = await axios.post(`${API}/media/upload-batch`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setProgressPct(Math.round((evt.loaded * 100) / evt.total));
        },
      });
      setUploaded(res.data.created || []);
      if (res.data.errors?.length) toast.warning(`${res.data.errors.length} erreur(s) d'upload`);
      toast.success(`${res.data.count} média(s) importé(s)`);
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload échoué");
    } finally {
      setUploading(false);
    }
  };

  const doAttach = async () => {
    if (selectedEntity.type === "none") { setStep(1); toast.success("Terminé (sans affectation)"); reset(); return; }
    const entityId = selectedEntity.type === "trip_day" ? selectedEntity.day_id : selectedEntity.entity_id;
    if (!entityId) { toast.error("Sélectionnez une cible"); return; }
    try {
      await batchAttachMedia({
        media_ids: uploaded.map((m) => m.id),
        attachments: [{ entity_type: selectedEntity.type, entity_id: entityId, order: 0 }],
      });
      toast.success("Médias affectés");
      reset();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur affectation");
    }
  };

  const reset = () => {
    setStep(1);
    setFiles([]);
    setUploaded([]);
    setProgressPct(0);
    setSelectedEntity({ type: "none", entity_id: null, day_id: null });
    setTripDays([]);
  };

  return (
    <div className="p-6" data-testid="media-import-page">
      <h1 className="font-heading text-2xl font-semibold text-primary mb-6 flex items-center gap-2">
        <UploadCloud className="h-6 w-6" /> Ajouter des médias
      </h1>

      <div className="flex items-center gap-2 text-sm mb-6">
        <span className={`px-2 py-1 rounded ${step === 1 ? "bg-primary text-white" : "bg-secondary text-primary"}`}>1. Upload</span>
        <ArrowRight className="h-4 w-4 text-muted-foreground" />
        <span className={`px-2 py-1 rounded ${step === 2 ? "bg-primary text-white" : "bg-secondary text-primary"}`}>2. Affectation</span>
      </div>

      {step === 1 && (
        <div className="bg-white border border-border rounded-lg p-6">
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="image/*,video/mp4,video/quicktime,video/webm"
            onChange={onFileChange}
            className="hidden"
            data-testid="file-input"
          />
          <div
            onClick={() => inputRef.current?.click()}
            className="border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-accent transition-colors"
            data-testid="dropzone"
          >
            <UploadCloud className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-sm font-medium">Cliquer pour sélectionner des fichiers</p>
            <p className="text-xs text-muted-foreground mt-1">Photos (jpg/png/webp/heic) + vidéos (mp4/mov/webm)</p>
          </div>

          {files.length > 0 && (
            <div className="mt-6">
              <div className="text-sm font-medium mb-2">Fichiers ({files.length}) :</div>
              <div className="space-y-1 max-h-64 overflow-y-auto border border-border rounded p-2">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm p-1" data-testid={`file-item-${i}`}>
                    {f.type.startsWith("video/") ? <VideoIcon className="h-4 w-4 text-purple-600" /> : <ImageIcon className="h-4 w-4 text-emerald-600" />}
                    <span className="flex-1 truncate">{f.name}</span>
                    <span className="text-xs text-muted-foreground">{(f.size / 1024).toFixed(0)} KB</span>
                    <button onClick={() => removeFile(i)}><X className="h-4 w-4 text-red-500" /></button>
                  </div>
                ))}
              </div>
              {uploading && (
                <div className="mt-3">
                  <div className="h-2 bg-secondary rounded overflow-hidden">
                    <div className="h-full bg-primary transition-all" style={{ width: `${progressPct}%` }} />
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">Upload {progressPct}%</div>
                </div>
              )}
              <div className="flex gap-2 mt-4">
                <Button onClick={doUpload} disabled={uploading} className="gap-2" data-testid="btn-upload">
                  {uploading ? <><Loader2 className="h-4 w-4 animate-spin" /> Upload...</> : <><UploadCloud className="h-4 w-4" /> Importer {files.length} fichier(s)</>}
                </Button>
                <Button variant="outline" onClick={() => setFiles([])} disabled={uploading}>Vider</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {step === 2 && (
        <div className="bg-white border border-border rounded-lg p-6" data-testid="step-affectation">
          <div className="flex items-center gap-2 text-sm text-green-700 mb-4">
            <Check className="h-4 w-4" /> {uploaded.length} média(s) importé(s) avec succès
          </div>

          <label className="block text-sm font-medium mb-2">Affecter à :</label>
          <Select value={selectedEntity.type} onValueChange={(v) => setSelectedEntity({ type: v, entity_id: null, day_id: null })}>
            <SelectTrigger data-testid="select-entity-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Aucune affectation</SelectItem>
              <SelectItem value="album">Album</SelectItem>
              <SelectItem value="article">Article</SelectItem>
              <SelectItem value="trip">Carnet de voyage</SelectItem>
              <SelectItem value="trip_day">Journée d'un carnet</SelectItem>
              <SelectItem value="destination">Destination</SelectItem>
              <SelectItem value="partner">Partenaire</SelectItem>
              <SelectItem value="deal">Deal</SelectItem>
            </SelectContent>
          </Select>

          {selectedEntity.type === "album" && (
            <div className="mt-3">
              <Select value={selectedEntity.entity_id || ""} onValueChange={(v) => setSelectedEntity({ ...selectedEntity, entity_id: v })}>
                <SelectTrigger><SelectValue placeholder="Choisir un album" /></SelectTrigger>
                <SelectContent>{albums.map((a) => <SelectItem key={a.id} value={a.id}>{a.name || a.title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {selectedEntity.type === "article" && (
            <div className="mt-3">
              <Select value={selectedEntity.entity_id || ""} onValueChange={(v) => setSelectedEntity({ ...selectedEntity, entity_id: v })}>
                <SelectTrigger><SelectValue placeholder="Choisir un article" /></SelectTrigger>
                <SelectContent>{articles.map((a) => <SelectItem key={a.id} value={a.id}>{a.title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {(selectedEntity.type === "trip" || selectedEntity.type === "trip_day") && (
            <div className="mt-3">
              <Select value={selectedEntity.entity_id || ""} onValueChange={(v) => setSelectedEntity({ ...selectedEntity, entity_id: v, day_id: null })}>
                <SelectTrigger><SelectValue placeholder="Choisir un carnet" /></SelectTrigger>
                <SelectContent>{trips.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}</SelectContent>
              </Select>
              {selectedEntity.type === "trip_day" && selectedEntity.entity_id && (
                <Select value={selectedEntity.day_id || ""} onValueChange={(v) => setSelectedEntity({ ...selectedEntity, day_id: v })}>
                  <SelectTrigger className="mt-2"><SelectValue placeholder="Choisir une journée" /></SelectTrigger>
                  <SelectContent>{tripDays.map((d) => <SelectItem key={d.id} value={d.id}>Jour {d.day_number} — {d.stop_name}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
          )}
          {selectedEntity.type === "destination" && (
            <div className="mt-3">
              <Select value={selectedEntity.entity_id || ""} onValueChange={(v) => setSelectedEntity({ ...selectedEntity, entity_id: v })}>
                <SelectTrigger><SelectValue placeholder="Choisir une destination" /></SelectTrigger>
                <SelectContent>{destinations.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {selectedEntity.type === "partner" && (
            <div className="mt-3">
              <Select value={selectedEntity.entity_id || ""} onValueChange={(v) => setSelectedEntity({ ...selectedEntity, entity_id: v })}>
                <SelectTrigger><SelectValue placeholder="Choisir un partenaire" /></SelectTrigger>
                <SelectContent>{partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {selectedEntity.type === "deal" && (
            <div className="mt-3">
              <Select value={selectedEntity.entity_id || ""} onValueChange={(v) => setSelectedEntity({ ...selectedEntity, entity_id: v })}>
                <SelectTrigger><SelectValue placeholder="Choisir un deal" /></SelectTrigger>
                <SelectContent>{deals.map((d) => <SelectItem key={d.id} value={d.id}>{d.title || d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}

          <div className="flex gap-2 mt-6">
            <Button onClick={doAttach} className="gap-2" data-testid="btn-finish-import">
              <Check className="h-4 w-4" /> Terminer
            </Button>
            <Button variant="outline" onClick={reset}>Nouveau lot</Button>
          </div>
        </div>
      )}
    </div>
  );
}
